import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { message, notification } from 'antd';
import { handle401Error, isLoginPage, hasValidToken } from '@/utils/auth';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  timeout: 200000,
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType } =
        res as unknown as ResponseStructure;
      if (!success) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        message.error(errorMessage);
        // throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        const { status } = error.response;

        // 专门处理401错误
        if (status === 401) {
          handle401Error();
          return;
        }

        // 处理其他HTTP错误状态码
        if (status >= 500) {
          message.error(`服务器错误 (${status})`);
        } else if (status >= 400) {
          message.error(`请求错误 (${status})`);
        } else {
          message.error(`响应状态错误: ${status}`);
        }
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // 这通常是网络连接问题、超时或代理问题
        if (error.code === 'ECONNABORTED') {
          message.error('请求超时，请检查网络连接后重试！');
        } else if (error.code === 'NETWORK_ERROR') {
          message.error('网络连接失败，请检查网络设置！');
        } else {
          message.error('网络请求失败，请检查网络连接或稍后重试！');
        }
      } else {
        // 发送请求时出了点问题
        message.error('请求配置错误，请联系管理员！');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 拦截请求配置，进行个性化处理。
      const url = config?.url;

      // 如果有token，则添加到请求头
      if (hasValidToken()) {
        const headers = {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        };
        const options = { ...config, headers };
        return { ...options, url };
      } else {
        // 如果没有token且不在登录页面，跳转到登录页
        if (!isLoginPage()) {
          history.push(loginPath);
        }
      }

      return { ...config, url };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      return response;
    },
  ],
};
